"use client";
import React, { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useRouter } from "next/router";

import { AutoComplete } from "primereact/autocomplete";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";

import { IRecruiter } from "@/types/formInfo.interface";
import { objectToArray } from "@/utils/objectToArray";

import useSearchField from "@/hooks/useSearchField";
import { useRecruiterData } from "@/hooks/useRecruiterData";

import { OnboardingRecruiterServices } from "@/services/onboardingRecruiter.services";
import { OnboardingServices } from "@/services/onboarding.services";
import { useUser } from "@/context/AuthContext";
import { generateRandomId } from "@/utils/generateRandomId";

const RecruiterWatingList = () => {
  const [showModal, setShowModal] = useState(false);

  const router = useRouter();

  const { data: commonData } = useQuery(
    ["commonData"],
    OnboardingServices.getCommonData
  );

  const { filteredList, searchList } = useSearchField(commonData?.companies);

  const hideModal = () => {
    setShowModal(false);
    router.push("/");
  };

  const {
    reset,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      title: "",
      launchEmail: true,
      companyInformation: {
        name: "",
        city: "",
        country: "",
        website: "",
        email: "",
      },
    },
  });

  const { mutate, isLoading, error } = useMutation(
    (formData: any) =>
      OnboardingRecruiterServices.sendWatingListInformation({
        ...formData,
        id: generateRandomId(),
      }),
    {
      onSuccess: (data) => {
        setShowModal(true);
      },
    }
  );

  const onSubmit = async (formData) => {
    mutate({
      ...formData,
      companyInformation: {
        ...formData.companyInformation,
      },
    });
  };

  return (
    <>
      <div className="flex flex-column align-items-center justify-content-center w-full">
        <div className="w-full surface-card flex flex-column align-items-center gap-5">
          <div className="text-900 text-3xl font-medium mb-3 text-primary">
            Waiting list for portfolii Recruiter Beta
          </div>
          <div className="w-full">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="w-full p-fluid mb-3">
                <Controller
                  name="companyInformation.name"
                  control={control}
                  rules={{
                    required: "Company name is required.",
                  }}
                  render={({ field }) => (
                    <>
                      <label
                        htmlFor={field.name}
                        className="block font-sm mb-2 text-primary"
                      >
                        Company Name <span className="text-red-500">*</span>
                      </label>

                      <AutoComplete
                        {...field}
                        inputId={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        inputRef={field.ref}
                        suggestions={filteredList}
                        completeMethod={(e) => searchList(e.query)}
                        className={`w-full flex ${
                          errors.companyInformation?.name?.message &&
                          "p-invalid"
                        }`}
                      />
                    </>
                  )}
                />
              </div>
              <div className="w-full mb-3">
                <Controller
                  name="companyInformation.website"
                  control={control}
                  rules={{
                    required: "Website is required.",
                  }}
                  render={({ field }) => (
                    <>
                      <label
                        htmlFor={field.value}
                        className="block font-sm mb-2 text-primary"
                      >
                        website <span className="text-red-500">*</span>
                      </label>
                      <div className="p-inputgroup">
                        <span className="p-inputgroup-addon">www</span>
                        <InputText
                          placeholder="www.google.com"
                          {...field}
                          value={field.value}
                          id={field.name}
                          name={field.name}
                          className={`w-full ${
                            errors.companyInformation?.website?.message &&
                            "p-invalid"
                          }`}
                        />
                      </div>
                    </>
                  )}
                />
              </div>

              <div className="w-full mb-3">
                <Controller
                  name="companyInformation.email"
                  control={control}
                  rules={{
                    required: "Company email is required.",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                      message: "Invalid email address",
                    },
                  }}
                  render={({ field }) => (
                    <>
                      <label
                        htmlFor={field.name}
                        className="block font-sm mb-2 text-primary"
                      >
                        Your Company Email
                        <span className="text-red-500"> *</span>
                      </label>

                      <InputText
                        {...field}
                        value={field.value}
                        id={field.name}
                        type="text"
                        className={`w-full ${
                          errors.companyInformation?.email?.message &&
                          "p-invalid"
                        }`}
                      />
                    </>
                  )}
                />
              </div>

              <div className="flex flex-column lg:flex-row mt-4 mb-3">
                <div className="lg:pr-3 mb-3 lg:mb-0 w-full">
                  <Controller
                    name="firstName"
                    control={control}
                    rules={{
                      required: "FirstName is required.",
                    }}
                    render={({ field }) => (
                      <>
                        <label
                          htmlFor="firstName"
                          className="block font-sm mb-2 text-primary"
                        >
                          First Name <span className="text-red-500">*</span>
                        </label>

                        <InputText
                          {...field}
                          value={field.value}
                          id="firstName"
                          type="text"
                          className={`w-full ${
                            errors.firstName?.message && "p-invalid"
                          }`}
                        />
                      </>
                    )}
                  />
                </div>
                <div className="w-full lg:mb-0">
                  <Controller
                    name="lastName"
                    control={control}
                    rules={{
                      required: "Last name is required.",
                    }}
                    render={({ field }) => (
                      <>
                        <label
                          htmlFor="lastName"
                          className="block font-sm mb-2 text-primary"
                        >
                          Last Name <span className="text-red-500">*</span>
                        </label>

                        <InputText
                          {...field}
                          value={field.value}
                          id="firstName"
                          type="text"
                          className={`w-full ${
                            errors.lastName?.message && "p-invalid"
                          }`}
                        />
                      </>
                    )}
                  />
                </div>
              </div>

              <div className="flex flex-column lg:flex-row mb-3">
                <div className="lg:pr-3 w-full mb-3 lg:mb-0">
                  <Controller
                    name="title"
                    control={control}
                    rules={{
                      required: "Title is required.",
                    }}
                    render={({ field }) => (
                      <>
                        <label
                          htmlFor={field.name}
                          className="block font-sm mb-2 text-primary"
                        >
                          Title <span className="text-red-500">*</span>
                        </label>

                        <InputText
                          {...field}
                          value={field.value}
                          id={field.name}
                          type="text"
                          className={`w-full ${
                            errors.title?.message && "p-invalid"
                          }`}
                        />
                      </>
                    )}
                  />
                </div>
                <div className="w-full">
                  <Controller
                    name="companyInformation.country"
                    control={control}
                    rules={{
                      required: "country is required.",
                    }}
                    render={({ field }) => (
                      <>
                        <label
                          htmlFor={field.name}
                          className="block font-sm mb-2 text-primary"
                        >
                          Country <span className="text-red-500">*</span>
                        </label>
                        <Dropdown
                          {...field}
                          value={field.value}
                          optionLabel="name"
                          placeholder="Select a Country"
                          id="country"
                          name="country"
                          options={objectToArray(commonData?.countries)}
                          className={`w-full ${
                            errors.companyInformation?.country?.message &&
                            "p-invalid"
                          }`}
                        />
                      </>
                    )}
                  />
                </div>
              </div>

              <div className=" mb-3">
                <Controller
                  name="launchEmail"
                  control={control}
                  render={({ field, fieldState }) => (
                    <>
                      <Checkbox
                        inputId={field.name}
                        checked={field.value}
                        inputRef={field.ref}
                        onChange={(e) => field.onChange(e.checked)}
                      />
                      <label
                        htmlFor={field.name}
                        className="ml-2 text-primary font-md"
                      >
                        Email me when portfolii Recruiter Beta is launched
                      </label>
                    </>
                  )}
                />
              </div>

              <Button
                label="Sign up to the waiting list"
                className="mt-2"
                loading={isLoading}
              />

              <p className="p-invalid text-center text-red-500 m-0">
                {error?.message}
              </p>
            </form>
          </div>
        </div>
      </div>
      <Dialog
        draggable={false}
        onHide={hideModal}
        visible={showModal}
        className="w-11 lg:w-6"
        closable={true}
        blockScroll={true}
      >
        <div className="flex align-items-center justify-content-center flex-column text-center">
          <i className="pi pi-check-circle text-green-400 text-8xl"></i>
          <h2 className="text-center text-primary font-normal">
            Thanks for registering to our waiting list.
          </h2>
          <p className="text-bluegray-400 mb-4">
            You will be the first to know when we launch portfolii recruiters.
          </p>
        </div>
      </Dialog>
    </>
  );
};

export default RecruiterWatingList;
